import axiosApiClient from "..";

import { handleError } from "../../utils/errors";

const BASE_URL = "business/marketplace";

class MarketplaceService {
  getOrders = async ({ page, sortBy, order }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/order?page=${page}&sortBy=${sortBy}&order=${order}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data, maxReached, total, perPage } = response.data;
      return {
        data,
        total,
        perPage,
        maxReached,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching  marketplace orders.",
      );
    }
  };

  getFavoriteMarketplaceItemsAll = async ({
    page,
    sortBy,
    order,
    search,
    category,
  }) => {
    try {
      let params = "";
      if (category) {
        params = `&categoryId=${category}`;
      }

      const response = await axiosApiClient.get(
        `${BASE_URL}/favorite?page=${page}&sortBy=${sortBy}&order=${order}&search=${search}` +
          params,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data, maxReached, total, perPage } = response.data;
      return {
        data,
        total,
        perPage,
        maxReached,
        page,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching favorite marketplace items.",
      );
    }
  };

  getCountFavoriteMarketplaceItems = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/favorite/count`);
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching count of favorite marketplace items.",
      );
    }
  };

  getOrder = async (id) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/order/${id}`);
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching marketplace order.",
      );
    }
  };

  getAllMarketplaceItems = async ({
    search,
    page,
    sortBy,
    order,
    category,
    priceFrom,
    priceTo,
  }) => {
    if (!search) {
      search = "";
    }

    if (!sortBy) {
      sortBy = "priority";
    }

    if (!order) {
      order = "ASC";
    }

    if (!category) {
      category = "";
    }
    let query = `&search=${search}&sortBy=${sortBy}&order=${order}&category=${category}`;
    if (priceFrom) {
      query += `&priceFrom=${priceFrom}`;
    }
    if (priceTo) {
      query += `&priceTo=${priceTo}`;
    }

    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/items/all?page=${page}` + query,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      response.data.page = page;
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching marketplace items.",
      );
    }
  };

  createOrder = async (order) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/order`, order);
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating order.");
    }
  };

  setPriorityMarketplaceItem = async ({ id, priority }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/item/${id}/priority`,
        { priority },
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while setting priority marketplace item.",
      );
    }
  };

  toggleFavoriteMarketplaceItem = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/favorite`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return requestData;
    } catch (err) {
      throw handleError(err, "Error occurred while toggle favorite item.");
    }
  };

  createItem = async (item) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/items`, item);
      if (response.status !== 201) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating item.");
    }
  };

  removeItem = async (itemId) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/items/${itemId}`,
      );
      if (response.status !== 201) {
        throw new Error();
      }

      return { itemId };
    } catch (err) {
      throw handleError(err, "Error occurred while deleting item.");
    }
  };

  getSubcategories = async ({ categories }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/subcategories?categories=${categories}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching marketplace item subcategories.",
      );
    }
  };

  createSubcategory = async ({ value, marketplaceCategoryId }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/subcategories`, {
        value,
        marketplaceCategoryId,
      });
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while creating marketplace item subcategory.",
      );
    }
  };

  getCategories = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/categories`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching marketplace item categories.",
      );
    }
  };

  createCategory = async (body) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/categories`,
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while creating marketplace item category.",
      );
    }
  };

  updateOrderState = async ({ id, state, paymentStatus }) => {
    try {
      const response = await axiosApiClient.put(`${BASE_URL}/order/${id}`, {
        state,
        paymentStatus,
      });

      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating order state.");
    }
  };

  getMarketplaceItemById = async (itemId) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/items/${itemId}`);
      if (response.status !== 200) {
        throw new Error();
      }

      const { data, message } = response.data;

      return { data, message };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching item by id.");
    }
  };

  getSubcategoriesList = async () => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/subcategories-list`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data.result;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching marketplace item subcategories with categories.",
      );
    }
  };

  deleteVariable = async (id) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/subcategory-item/${id}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return {
        subcategoryItemId: id,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while deleting subcategory items.",
      );
    }
  };

  deleteSubcategory = async (id) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/subcategory/${id}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return {
        subcategoryId: id,
      };
    } catch (err) {
      throw handleError(err, "Error occurred while deleting subcategory.");
    }
  };

  deleteCategory = async (id) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/category/${id}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting category.");
    }
  };

  createVariable = async ({ value, subcategoryId }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/add-subcategory-item`,
        { value, subcategoryId },
      );
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data.subcategoryItem;
    } catch (err) {
      throw handleError(err, "Error occurred while creating variable.");
    }
  };

  editVariable = async ({ value, subcategoryItemId }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/subcategory-item/${subcategoryItemId}`,
        { value },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating variable.");
    }
  };

  editSubcategory = async ({ value, subcategoryId }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/subcategory/${subcategoryId}`,
        { value },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating subcategory.");
    }
  };

  editCategory = async ({ name, nameAr, categoryId, subcategories }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/category/${categoryId}`,
        { name, nameAr, subcategories },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating category.");
    }
  };

  updateCategoryPriority = async ({ newPriorities }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/categories/priority`,
        { newPriorities },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating categories priority.",
      );
    }
  };

  getAttributesByCategory = async (categoryId) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/subcategories/${categoryId}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating categories priority.",
      );
    }
  };

  editItem = async ({ itemId, item }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/items/${itemId}`,
        item,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while editing an item.");
    }
  };

  sendShareCartProductEmail = async ({ marketplaceItemId, email }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/share-cart-product`,
        { marketplaceItemId, email },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while sending an email for share cart product.",
      );
    }
  };

  validatePayment = async ({ paymentChargeId }) => {
    try {
      const body = {
        paymentChargeId,
      };
      const response = await axiosApiClient.post(
        BASE_URL + "/validate-payment",
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while choosing a plan");
    }
  };

  generateInvoice = async ({
    paymentChargeId,
    companyName,
    companyVat,
    companyCountry,
    companyAddress,
    companyPostalCode,
  }) => {
    try {
      const body = {
        paymentChargeId,
        companyName,
        companyVat,
        companyCountry,
        companyAddress,
        companyPostalCode,
      };
      const response = await axiosApiClient.post(
        BASE_URL + "/generate-invoice",
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while generating an invoice");
    }
  };

  getMarketplaceOrdersSuperAdmin = async ({ page, sortBy, order }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/order-admin?page=${page}&sortBy=${sortBy}&order=${order}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data, maxReached, total, perPage } = response.data;
      return {
        data,
        total,
        perPage,
        maxReached,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching super admin marketplace orders.",
      );
    }
  };

  generateInvoiceMarketplaceOrder = async ({
    marketplaceOrderId,
    companyName,
    companyVat,
    companyCountry,
    companyAddress,
    companyPostalCode,
  }) => {
    try {
      const body = {
        companyName,
        companyVat,
        companyCountry,
        companyAddress,
        companyPostalCode,
      };
      const response = await axiosApiClient.post(
        BASE_URL + `/generate-invoice/${marketplaceOrderId}`,
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while generating an invoice");
    }
  };
  getOrderPackages = async () => {
    try {
      const response = await axiosApiClient.get(BASE_URL + "/order-packages");
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching order packages");
    }
  };

  getOrdersForDesigner = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/order-ops`);
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching marketplace orders.",
      );
    }
  };

  updateOrderStateDesigner = async (id, body) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/order-state/${id}`,
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating state for marketplace order.",
      );
    }
  };

  addDesignerNote = async (id, body) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/order-item/note/${id}`,
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(err, "Error occurred while updating order note.");
    }
  };

  addPackageNote = async (id, body) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/order-package/note/${id}`,
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(err, "Error occurred while updating package note.");
    }
  };

  addProductionNote = async (id, body) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/order-item/production-note/${id}`,
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating order production note.",
      );
    }
  };

  addProductionPackageNote = async (id, body) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/order-package/production-note/${id}`,
        body,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating production package note.",
      );
    }
  };

  addDesignerDesignFile = async ({ id, formData }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/order-item/design/${id}`,
        formData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while uploading design file for marketplace order.",
      );
    }
  };

  addPackageDesignFile = async ({ id, formData }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/order-package/design/${id}`,
        formData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while uploading design file for order package.",
      );
    }
  };

  sendDesignsToReview = async ({ id }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/order-review/${id}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating state for marketplace order designs.",
      );
    }
  };

  orderNextState = async ({ id }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/order-next-state/${id}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const { data } = response.data;

      return {
        data,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating state for marketplace order state.",
      );
    }
  };

  acceptDesign = async (id) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/order-item-design/${id}/accept`,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while accepting order.");
    }
  };

  rejectDesign = async ({ id, note }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/order-item-design/${id}/reject`,
        { note },
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while rejecting order.");
    }
  };

  acceptPackageDesign = async (id) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/order-package-design/${id}/accept`,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while accepting package order.");
    }
  };

  rejectPackageDesign = async ({ id, note }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/order-package-design/${id}/reject`,
        { note },
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while rejecting package order.");
    }
  };

  getMarketplaceItemUnitTypes = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/item-unit-types`);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching marketplace item unit types.",
      );
    }
  };

  generateItemPhotos = async (body) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/generate-photo-items`,
        body,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      console.log(response.data);

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating AI photos.");
    }
  };

  generateItemDescription = async (body) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/generate-ai-description`,
        body,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      console.log(response.data);

      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while generating AI description.");
    }
  };

  updateInventoryQuantityItem = async () => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/items/availability`,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while updating inventory items quantity.",
      );
    }
  };
}

const marketplaceService = new MarketplaceService();

export default marketplaceService;
